import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReplaceStep } from '@tiptap/pm/transform';
import { isRemoteYjsTransaction } from './is-remote-yjs-transaction';
const SETTINGS_STORAGE_KEY = 'narrativebench_writing_settings';
const getSettingFromStorage = () => {
    try {
        const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
        if (stored) {
            const settings = JSON.parse(stored);
            const enabled = settings.replaceHyphensWithDashes === true;
            return enabled;
        }
    }
    catch (error) {
        console.error('[HyphenToDash] Error loading hyphen-to-dash setting:', error);
    }
    return true;
};
const isWordChar = (char) => {
    return /[a-zA-Z0-9]/.test(char);
};
const shouldReplaceHyphenWithChars = (charBefore, charAfter) => {
    if (charAfter === '') {
        return false;
    }
    if (charAfter === '>') {
        return false;
    }
    const hasWordBefore = charBefore !== '' && isWordChar(charBefore);
    const hasWordAfter = charAfter !== '' && isWordChar(charAfter);
    const isBetweenWords = hasWordBefore && hasWordAfter;
    if (isBetweenWords) {
        return false;
    }
    return true;
};
export const HyphenToDash = Extension.create({
    name: 'hyphenToDash',
    addOptions() {
        return {
            getSetting: undefined,
        };
    },
    onCreate() {
    },
    addProseMirrorPlugins() {
        const getSetting = this.options.getSetting || getSettingFromStorage;
        return [
            new Plugin({
                key: new PluginKey('hyphenToDash'),
                appendTransaction: (transactions, _oldState, newState) => {
                    const enabled = getSetting();
                    if (!enabled) {
                        return null;
                    }
                    const shouldProcess = transactions.some((tr) => {
                        if (tr.getMeta('hyphenToDash') || tr.getMeta('collapseSpaces') || tr.getMeta('automaticReplacements')) {
                            return false;
                        }
                        if (tr.getMeta('undo') || tr.getMeta('redo') || tr.getMeta('addToHistory') === false) {
                            return false;
                        }
                        if (isRemoteYjsTransaction(tr)) {
                            return false;
                        }
                        return tr.docChanged;
                    });
                    if (!shouldProcess) {
                        return null;
                    }
                    try {
                        const docSize = newState.doc.content.size;
                        const tr = newState.tr;
                        const replacements = [];
                        const safeTextBetween = (from, to, defaultText = '') => {
                            try {
                                if (from < 0 || to < 0 || from > docSize || to > docSize || from > to) {
                                    return defaultText;
                                }
                                return newState.doc.textBetween(from, to, '');
                            }
                            catch (error) {
                                return defaultText;
                            }
                        };
                        transactions.forEach((transaction) => {
                            if (!transaction.docChanged || isRemoteYjsTransaction(transaction)) {
                                return;
                            }
                            transaction.steps.forEach((step, stepIndex) => {
                                try {
                                    if (step instanceof ReplaceStep) {
                                        const from = step.from;
                                        const to = step.to;
                                        const slice = step.slice;
                                        if (from < 0 || to < 0 || from > docSize || to > docSize) {
                                            return;
                                        }
                                        const isDeletion = from < to && slice.content.size === 0;
                                        if (!isDeletion) {
                                            if (from > 0 && from <= docSize) {
                                                const charBefore = safeTextBetween(from - 1, from);
                                                if (charBefore === '-') {
                                                    const hyphenPos = from - 1;
                                                    const charBeforePos = hyphenPos > 0 ? hyphenPos - 1 : 0;
                                                    const charBeforeText = hyphenPos > 0 ? safeTextBetween(charBeforePos, hyphenPos) : '';
                                                    const insertedText = safeTextBetween(from, to);
                                                    const firstInsertedChar = insertedText.length > 0 ? insertedText[0] : '';
                                                    const charAfterText = firstInsertedChar !== '' ? firstInsertedChar : safeTextBetween(hyphenPos + 1, hyphenPos + 2);
                                                    const shouldReplace = shouldReplaceHyphenWithChars(charBeforeText, charAfterText);
                                                    if (shouldReplace) {
                                                        replacements.push({ pos: hyphenPos });
                                                    }
                                                }
                                            }
                                            const insertedText = safeTextBetween(from, to);
                                            if (insertedText.includes('-')) {
                                                let searchPos = from;
                                                while (searchPos < to && searchPos < docSize) {
                                                    const char = safeTextBetween(searchPos, searchPos + 1);
                                                    if (char === '-') {
                                                        const charBeforePos = searchPos > 0 ? searchPos - 1 : searchPos;
                                                        const charAfterPos = searchPos + 1;
                                                        const charBeforeText = searchPos > 0 ? safeTextBetween(charBeforePos, searchPos) : '';
                                                        const charAfterText = safeTextBetween(searchPos + 1, charAfterPos + 1);
                                                        if (shouldReplaceHyphenWithChars(charBeforeText, charAfterText)) {
                                                            replacements.push({ pos: searchPos });
                                                        }
                                                    }
                                                    searchPos += 1;
                                                }
                                            }
                                        }
                                    }
                                    else {
                                        const stepType = step.constructor.name;
                                        if (stepType.includes('Replace') || step.from !== undefined) {
                                            const stepAny = step;
                                            if (stepAny.from !== undefined && stepAny.to !== undefined) {
                                                const from = stepAny.from;
                                                const to = stepAny.to;
                                                if (from < 0 || to < 0 || from > docSize || to > docSize) {
                                                    return;
                                                }
                                                const slice = stepAny.slice;
                                                const isDeletion = from < to && (slice === undefined || slice.content === undefined || slice.content.size === 0);
                                                if (!isDeletion) {
                                                    if (from > 0 && from <= docSize) {
                                                        const charBefore = safeTextBetween(from - 1, from);
                                                        if (charBefore === '-') {
                                                            const hyphenPos = from - 1;
                                                            const charBeforePos = hyphenPos > 0 ? hyphenPos - 1 : 0;
                                                            const charBeforeText = hyphenPos > 0 ? safeTextBetween(charBeforePos, hyphenPos) : '';
                                                            const insertedText = safeTextBetween(from, to);
                                                            const firstInsertedChar = insertedText.length > 0 ? insertedText[0] : '';
                                                            const charAfterText = firstInsertedChar !== '' ? firstInsertedChar : safeTextBetween(hyphenPos + 1, hyphenPos + 2);
                                                            const shouldReplace = shouldReplaceHyphenWithChars(charBeforeText, charAfterText);
                                                            if (shouldReplace) {
                                                                replacements.push({ pos: hyphenPos });
                                                            }
                                                        }
                                                    }
                                                    const insertedText = safeTextBetween(from, to);
                                                    if (insertedText.includes('-')) {
                                                        let searchPos = from;
                                                        while (searchPos < to && searchPos < docSize) {
                                                            const char = safeTextBetween(searchPos, searchPos + 1);
                                                            if (char === '-') {
                                                                const charBeforePos = searchPos > 0 ? searchPos - 1 : searchPos;
                                                                const charAfterPos = searchPos + 1;
                                                                const charBeforeText = searchPos > 0 ? safeTextBetween(charBeforePos, searchPos) : '';
                                                                const charAfterText = safeTextBetween(searchPos + 1, charAfterPos + 1);
                                                                if (shouldReplaceHyphenWithChars(charBeforeText, charAfterText)) {
                                                                    replacements.push({ pos: searchPos });
                                                                }
                                                            }
                                                            searchPos += 1;
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                catch (error) {
                                    console.error(`[HyphenToDash] Error processing step ${stepIndex}:`, error);
                                }
                            });
                        });
                        if (replacements.length > 0) {
                            replacements.reverse().forEach(({ pos }) => {
                                try {
                                    if (pos >= 0 && pos < docSize) {
                                        tr.replaceWith(pos, pos + 1, newState.schema.text('—'));
                                    }
                                }
                                catch (error) {
                                    console.error(`[HyphenToDash] Error replacing at position ${pos}:`, error);
                                }
                            });
                            tr.setMeta('hyphenToDash', true);
                            return tr;
                        }
                        return null;
                    }
                    catch (error) {
                        console.error('[HyphenToDash] Error in appendTransaction:', error);
                        return null;
                    }
                },
            }),
        ];
    },
});
//# sourceMappingURL=hyphen-to-dash.js.map